import React, { useEffect, useState } from "react";
import {
    CircularProgress,
    colors, Drawer,
    IconButton,
    InputAdornment,
    makeStyles,
    TableRow,
    Typography,
} from "@material-ui/core";
import { FormattedMessage, useIntl } from "react-intl";
import { Box } from "@material-ui/core";
import voca from "voca";
import moment from "moment";
import ReplayIcon from "@material-ui/icons/Replay";
import { IoIosCalendar } from "react-icons/io";

import ICIPAdminService from "../../../../../../../services/ICIPAdminService";
import ExpandedContent from "../../../../../components/ExpandedContent";
import TableData, {
  StyledTableCell,
  StyledTableRow,
} from "../../../../../components/TableData";
import { InputDate } from "../../../../../../../components/input-date/InputDate";
import { YYYY_MM_DD_FORMAT } from "../../../../../../../settings/constants";
import {
  DefaultButton,
  OutlineButton,
} from "../../../../../components/Buttons";
import { formatPhoneNumber } from "../../../../../../../common/Hooks";
import DynamicPaginationActions from "../../../../../components/DynamicPaginationActions";
import { CustomTooltip } from "../../../../../components/Tooltip";
import Paginator, {DataTypeEnum} from "../../../../../../../models/Paginator";
import RaffleDrawParticipant from "../../../../../../../models/RaffleDrawParticipant";
import Divider from "@material-ui/core/Divider";
import {NotificationProvider} from "../../../../../../../context/NotificationContext";
import ExportRaffleParticipantsWrapper from "../../../../../components/ExportRaffleParticipantsWrapper";

interface RaffleParticipantStatProps {}

const useStyles = makeStyles(
  (theme) => ({
    gridContent: {
      padding: "0 8px",
    },
    error: {
      fontSize: 13,
      color: colors.red[700],
      textAlign: "left",
    },
    extraAction: {
      marginRight: 4,
      color: colors.grey[800],
    },
    tableWrapper: {
      "& table": {
        fontSize: 14,

        "& thead tr th": {
          padding: "14px 8px",
          textAlign: "left",
        },

        "& thead tr th:first-child": {
          padding: "14px 34px",
        },

        "& thead tr th:last-child": {
          textAlign: "right",
          padding: "14px 34px",
        },

        "& tbody tr td": {
          padding: "14px 8px",
          textAlign: "left",
        },

        "& tbody tr td:first-child": {
          padding: "14px 32px",
        },

        "& tbody tr td:last-child": {
          textAlign: "right",
          padding: "14px 34px",
        },
      },
    },
    resumeItem: {
      paddingRight: "16px",
      "& span": {
        fontWeight: 400,
        fontSize: "0.86rem",
      },
      "& h2": {
        fontWeight: 700,
        fontSize: "1.2rem",
          margin: 0,
          padding: 0,
      },
    },
      dialogDrawer: {
          "& .MuiBackdrop-root": {
              backgroundColor: "rgba(117, 117, 117, 0.2)",
              backdropFilter: "blur(1.5px)",
          },
      },
  }),
  { name: "icip-raffle-draw-flash-sms", flip: null }
);

const initialNumbers = () => {
  return {
    confirmed: 0,
    un_confirmed: 0,
    total_sms: 0,
  };
};

const initialPeriods = () => {
  return {
    start_date: moment().format(YYYY_MM_DD_FORMAT),
    end_date: moment().format(YYYY_MM_DD_FORMAT),
  };
};

enum ScreenStatus {
  loading,
  failure,
  succeed,
  initial,
  adding,
  paginating
}

interface LoadingState {
  status: ScreenStatus;
}

interface StateContent {
  participants?: Paginator;
  statNumbers?: any;
}

export const RaffleParticipantStat = (props: RaffleParticipantStatProps) => {
  const classes = useStyles();
  const intl = useIntl();
  // State
  const [periods, setPeriods] = useState((): any => {
    return initialPeriods();
  });
  const [loadingState, setLoadingState] = useState(
    (): LoadingState => {
      return { status: ScreenStatus.initial };
    }
  );
    const [openExportWrapper, setOpenExportWrapper] = useState(false);

  const [data, setData] = useState(
    (): StateContent => {
      return {
        participants: null,
        statNumbers: initialNumbers(),
      };
    }
  );

  const handleDateChange = (name: string, dateString: string) => {
    setPeriods({ ...periods, [name]: dateString });
  };

  const renderParticipantsTableHeader = () => {
    return (
      <TableRow>
        <StyledTableCell>#.</StyledTableCell>
        <StyledTableCell>
          <FormattedMessage id="sharedScreen.lastName" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.firstName" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.telephone" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.code" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.confirmedAt" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.lastRaffle" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.nextRaffle" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.addedAt" />
        </StyledTableCell>
      </TableRow>
    );
  };

  const renderParticipantsTableRows = () => {
      if(data.participants === null) {
          return (<></>);
      }

    return data.participants.items.map((item, index) => {
        let participant = item as RaffleDrawParticipant;
        let IsLoser = participant.prev_raffle_draw !== null && participant.winning_package === null;

      return (
        <StyledTableRow key={index}>
          <StyledTableCell component="td" scope="row">
            {participant.id}
          </StyledTableCell>
          <StyledTableCell component="td" scope="row">
            <Typography component="span" noWrap>
              {voca.titleCase(
                participant.last_name ? participant.last_name : "--"
              )}
            </Typography>
          </StyledTableCell>
          <StyledTableCell component="td" scope="row">
            <Typography component="span" noWrap>
              {voca.titleCase(
                participant.first_name ? participant.first_name : "--"
              )}
            </Typography>
          </StyledTableCell>
          <StyledTableCell component="td" scope="row">
            <Typography component="h5" style={{ fontWeight: 600 }} noWrap>
              {formatPhoneNumber(participant.telephone)}
            </Typography>
          </StyledTableCell>
          <StyledTableCell component="td" scope="row">
            <Typography component="h6" style={{ fontWeight: 600 }} noWrap>
              {participant.raffle_code}
            </Typography>
          </StyledTableCell>
          <StyledTableCell component="td" scope="row">
            <Typography component="h6" style={{ fontWeight: 600 }} noWrap>
              {participant.confirmed_at !== null
                ? moment
                    .utc(participant.confirmed_at)
                    .local()
                    .format("DD, MMM yyyy @HH:mm")
                : "--"}
            </Typography>
          </StyledTableCell>
            <StyledTableCell component="td" scope="row">
                <Typography component="h6" style={{ fontWeight: 600, color: IsLoser ? colors.red[800] : colors.green[800] }} noWrap>
                    {participant.next_raffle_draw !== null
                        ? moment
                            .utc(participant.next_raffle_draw.raffle_draw_date)
                            .local()
                            .format("DD, MMM yyyy @HH:mm")
                        : "--"}
                </Typography>
            </StyledTableCell>
            <StyledTableCell component="td" scope="row">
                <Typography component="h6" style={{ fontWeight: 600 }} noWrap>
                    {participant.prev_raffle_draw !== null
                        ? moment
                            .utc(participant.prev_raffle_draw.raffle_draw_date)
                            .local()
                            .format("DD, MMM yyyy @HH:mm")
                        : "--"}
                </Typography>
            </StyledTableCell>
          <StyledTableCell component="td" scope="row">
            <Typography component="span" noWrap>
              {moment
                .utc(participant.created_at)
                .local()
                .format("DD, MMM yyyy @HH:mm")}
            </Typography>
          </StyledTableCell>
        </StyledTableRow>
      );
    });
  };

  const loadParticipants = () => {
    setLoadingState({ ...loadingState, status: ScreenStatus.loading });

    const response = ICIPAdminService.raffleDrawParticipationResume(periods);
    response.then((response) => {
      if (response.isSuccess && response.data.code === 100) {
          let data = JSON.parse(JSON.stringify(response.data.data));
          let paginator = Paginator.fromJson(
              data.paginator,
              DataTypeEnum.raffleParticipants
          );
        // Compute stat numbers
        let statNumbers = data.stats;

        setData({ participants: paginator, statNumbers: statNumbers });
        setLoadingState({ ...loadingState, status: ScreenStatus.succeed });
      } else {
        setLoadingState({ ...loadingState, status: ScreenStatus.failure });
      }
    });
  };

    const paginatingData = (page?: number) => {
        setLoadingState({ ...loadingState, status: ScreenStatus.paginating });

        let requestData = {...periods, page: page};
        const response = ICIPAdminService.raffleDrawParticipationResume(requestData);
        response.then((response) => {
            if (response.isSuccess && response.data.code === 100) {
                let data = JSON.parse(JSON.stringify(response.data.data));
                let paginator = Paginator.fromJson(
                    data.paginator,
                    DataTypeEnum.raffleParticipants
                );
                // Compute stat numbers
                let statNumbers = data.stats;

                setData({ participants: paginator, statNumbers: statNumbers });
                setLoadingState({ ...loadingState, status: ScreenStatus.succeed });
            } else {
                setLoadingState({ ...loadingState, status: ScreenStatus.failure });
            }
        });
    };

  useEffect(() => {
    loadParticipants();
  }, []);

  return (
    <>
      <ExpandedContent
        withPadding
        title={intl.formatMessage({
          id: "sharedScreen.participationStatistics",
        })}
        defaultExpanded={true}
      >
        <Box className={classes.tableWrapper}>
          <Box
            mb={2}
            display="flex"
            alignItems="center"
            style={{ padding: "0 32px" }}
          >
            <Box style={{ flexGrow: 1 }}>
              <Box display="flex" alignItems="center">
                <Box style={{ width: "220px" }}>
                  <InputDate
                    maxDate=""
                    onDateChange={(name, date) => handleDateChange(name, date)}
                    inputProps={{
                      fullWidth: true,
                      id: "start_date",
                      name: "start_date",
                      placeholder: intl.formatMessage({
                        id: "sharedScreen.startDate",
                      }),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IoIosCalendar />
                        </InputAdornment>
                      ),
                      value: periods.start_date,
                    }}
                    pickerProps={{
                      format: YYYY_MM_DD_FORMAT,
                      disabled: false,
                      showToday: false,
                    }}
                  />
                </Box>
                <Box pl={1} style={{ width: "220px" }}>
                  <InputDate
                    maxDate=""
                    onDateChange={(name, date) => handleDateChange(name, date)}
                    inputProps={{
                      fullWidth: true,
                      id: "end_date",
                      name: "end_date",
                      placeholder: intl.formatMessage({
                        id: "sharedScreen.endDate",
                      }),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IoIosCalendar />
                        </InputAdornment>
                      ),

                      value: periods.end_date,
                    }}
                    pickerProps={{
                      format: YYYY_MM_DD_FORMAT,
                      disabled: false,
                      showToday: false,
                    }}
                  />
                </Box>
              </Box>
            </Box>
            <Box display="flex" alignItems="center">
              {loadingState.status === ScreenStatus.loading ? (
                <Box display="flex" justifyContent="center" pl={4} pr={4}>
                  <CircularProgress color="secondary" size={20} thickness={4} />
                </Box>
              ) : (
                <></>
              )}
              <Box>
                <OutlineButton
                  disabled={loadingState.status === ScreenStatus.loading || loadingState.status === ScreenStatus.paginating}
                  onClick={(_) => setOpenExportWrapper(true)}
                >
                  <FormattedMessage id="sharedScreen.exportRaffleParticipants" />
                </OutlineButton>
              </Box>
              <Box ml={1}>
                <DefaultButton
                  disabled={loadingState.status === ScreenStatus.loading || loadingState.status === ScreenStatus.paginating}
                  onClick={(_) => loadParticipants()}
                >
                  <FormattedMessage id="sharedScreen.refresh" />
                </DefaultButton>
              </Box>
            </Box>
          </Box>
          <Box mb={4} display="flex" alignItems="center" style={{ padding: "0px 16px 0px 32px" }}>
            <Box
              display="flex"
              alignItems="center"
              mr={2}
            >
              <Box component="div" className={classes.resumeItem}>
                  <Typography component="h2">
                      {data.statNumbers.confirmed}
                  </Typography>
                <Typography component="span">
                  <FormattedMessage id="sharedScreen.totalConfirmed" />
                </Typography>
              </Box>
              <Box component="div" className={classes.resumeItem}>
                  <Typography component="h2">
                      {data.statNumbers.un_confirmed}
                  </Typography>
                <Typography component="span">
                  <FormattedMessage id="sharedScreen.totalNonConfirmed" />
                </Typography>
              </Box>
              <Box component="div" className={classes.resumeItem}>
                  <Typography component="h2">
                      {data.statNumbers.total}
                  </Typography>
                <Typography component="span">
                  <FormattedMessage id="sharedScreen.totalParticipantSms" />
                </Typography>
              </Box>
            </Box>
              <Divider orientation="vertical" variant="middle" flexItem light style={{height: "30px", marginTop: "12px"}}/>
            <Box display="flex" alignItems="center" ml={2}>
              <CustomTooltip
                title={intl.formatMessage({ id: "sharedScreen.refresh" })}
                arrow
              >
                <IconButton
                  disabled={false}
                  className={classes.extraAction}
                  onClick={(_) => loadParticipants()}
                >
                  <ReplayIcon fontSize="small" />
                </IconButton>
              </CustomTooltip>
                <Box ml={2}>
                    <DynamicPaginationActions
                        loading={loadingState.status === ScreenStatus.paginating}
                        enabled={loadingState.status !== ScreenStatus.paginating}
                        pageNumber={data.participants !== null ? data.participants.page : 1}
                        perPage={data.participants !== null ? data.participants.per_page : 0}
                        totalPages={data.participants !== null ? data.participants.total : 0}
                        handlePrevAction={
                            data.participants !== null && data.participants.prev_page !== null
                                ? () => {
                                    paginatingData(data.participants.page - 1,);
                                }
                                : null
                        }
                        handleNextAction={
                            data.participants !== null && data.participants.next_page !== null
                                ? () => {
                                    paginatingData(data.participants.page + 1);
                                }
                                : null
                        }
                    />
                </Box>
            </Box>
          </Box>
          <TableData
            rows={renderParticipantsTableRows()}
            header={renderParticipantsTableHeader()}
          />
        </Box>
      </ExpandedContent>
        <Drawer
            className={classes.dialogDrawer}
            anchor="right"
            open={openExportWrapper}
            elevation={0}
            onClose={(_) => setOpenExportWrapper(false)}
            PaperProps={{
                style: { width: "34%" },
            }}
        >
            <NotificationProvider>
                <ExportRaffleParticipantsWrapper
                    onClose={() => setOpenExportWrapper(false)}
                />
            </NotificationProvider>
        </Drawer>
    </>
  );
};
